import { dateStringAtDay } from './calendar-day.js';
import { getLocale, t } from '../../shared/i18n/index.js';

const DAY_MS = 86400000;
const MIN_BOUNDARY_GAP = 12;
// Real pixels per calendar period, independent of the total explored range.
const LEVELS = [
  { unit: 'year', days: 365, minWidth: 0, textWidth: 35 },
  { unit: 'quarter', days: 90, minWidth: 36, textWidth: 25 },
  { unit: 'month', days: 28, minWidth: 42, textWidth: 30 },
  { unit: 'week', days: 7, minWidth: 42, textWidth: 28 },
  { unit: 'day', days: 1, minWidth: 18, textWidth: 20 },
];
// Short month names in the app's language; the formatter is built once per language.
let monthFormat = null;
function monthLabel(date) {
  const locale = getLocale();
  if (monthFormat?.locale !== locale) {
    let format;
    try { format = new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : locale, { month: 'short', timeZone: 'UTC' }); }
    catch { format = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }); }
    monthFormat = { locale, format };
  }
  return monthFormat.format.format(date);
}

function periodStart(day, unit) {
  const date = new Date(Math.floor(day) * DAY_MS);
  if (unit === 'week') date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 6) % 7);
  else if (unit !== 'day') {
    date.setUTCDate(1);
    if (unit === 'quarter') date.setUTCMonth(Math.floor(date.getUTCMonth() / 3) * 3);
    if (unit === 'year') date.setUTCMonth(0);
  }
  return date;
}

function advance(date, unit) {
  if (unit === 'year') date.setUTCFullYear(date.getUTCFullYear() + 1);
  else if (unit === 'quarter' || unit === 'month') date.setUTCMonth(date.getUTCMonth() + (unit === 'quarter' ? 3 : 1));
  else date.setUTCDate(date.getUTCDate() + (unit === 'week' ? 7 : 1));
}

// Numbers are passed as text: they stay plain digits and skip number formatting per label.
function periodLabel(date, unit) {
  if (unit === 'year') return String(date.getUTCFullYear());
  if (unit === 'quarter') return t('sync.gantt.quarter', { n: String(Math.floor(date.getUTCMonth() / 3) + 1) });
  if (unit === 'month') return monthLabel(date);
  if (unit === 'day') return String(date.getUTCDate());
  // ISO weeks belong to the year containing Thursday, including across Jan 1.
  const thursday = new Date(date);
  thursday.setUTCDate(thursday.getUTCDate() + 3);
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1) / DAY_MS;
  return t('sync.gantt.week', { n: String(Math.floor((thursday / DAY_MS - yearStart) / 7) + 1) });
}

/** Exact calendar periods and grid lines. endDay is exclusive. Optional paint
 * bounds limit DOM work to the viewport without rounding any date boundaries. */
export function buildGanttCalendar(startDay, endDay, dayWidth, { from = startDay, to = endDay } = {}) {
  const levels = [], candidates = [];
  if (![startDay, endDay, dayWidth, from, to].every(Number.isFinite) || dayWidth <= 0 || endDay <= startDay) return { levels, lines: [] };
  from = Math.max(startDay, from);
  to = Math.min(endDay, to);
  for (const [rank, level] of LEVELS.entries()) {
    if (level.days * dayWidth < level.minWidth) continue;
    const periods = [];
    const date = periodStart(from, level.unit);
    while (date / DAY_MS < to) {
      const start = date / DAY_MS;
      const label = periodLabel(date, level.unit);
      const weekend = date.getUTCDay() === 0 || date.getUTCDay() === 6;
      advance(date, level.unit);
      const end = date / DAY_MS;
      const left = (Math.max(startDay, start) - startDay) * dayWidth;
      const width = (Math.min(endDay, end) - Math.max(startDay, start)) * dayWidth;
      periods.push({ start, end, left, width, label, weekend });
      // A clipped first period is not a real calendar boundary.
      if (start >= startDay) candidates.push({ day: start, x: left, unit: level.unit, rank, label, width, date: dateStringAtDay(start) });
    }
    levels.push({ ...level, periods });
  }
  // Coincident and nearly coincident boundaries use the strongest level.
  // E.g. a Monday beside the first of a month must not form a dark double line.
  candidates.sort((a, b) => a.day - b.day || a.rank - b.rank);
  const lines = [];
  for (const candidate of candidates) {
    while (lines.length && candidate.x - lines.at(-1).x < MIN_BOUNDARY_GAP && candidate.rank < lines.at(-1).rank) lines.pop();
    if (!lines.length || candidate.x - lines.at(-1).x >= MIN_BOUNDARY_GAP) lines.push(candidate);
  }
  return { levels, lines };
}
